/**
 * Notification Templates API Client with React Query Hooks
 *
 * Provides query and mutation hooks for notification template management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPut } from 'services/api';
import logger from 'utils/logger';
import type {
  NotificationTemplateListResponse,
  NotificationTemplate,
  NotificationTemplateUpdateRequest,
} from '../types/notification-template.types';

// Query key for notification templates
export const NOTIFICATION_TEMPLATES_QUERY_KEY = ['notificationTemplates'];

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetch all notification templates grouped by type
 */
const fetchNotificationTemplates = async (): Promise<NotificationTemplateListResponse> => {
  return apiGet<NotificationTemplateListResponse>('/api/admin/notification-templates');
};

/**
 * Update a notification template
 */
const updateNotificationTemplate = async (
  notificationType: string,
  languageCode: string,
  data: NotificationTemplateUpdateRequest
): Promise<NotificationTemplate> => {
  return apiPut<NotificationTemplate>(
    `/api/admin/notification-templates/${notificationType}/${languageCode}`,
    data
  );
};

// ============================================================================
// React Query Hooks
// ============================================================================

/**
 * Fetch notification templates
 */
export const useNotificationTemplates = () => {
  return useQuery({
    queryKey: NOTIFICATION_TEMPLATES_QUERY_KEY,
    queryFn: fetchNotificationTemplates,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
};

/**
 * Update a notification template
 */
export const useUpdateNotificationTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      notificationType,
      languageCode,
      data,
    }: {
      notificationType: string;
      languageCode: string;
      data: NotificationTemplateUpdateRequest;
    }) => updateNotificationTemplate(notificationType, languageCode, data),
    onSuccess: () => {
      // Invalidate notification templates to reload with updated values
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_TEMPLATES_QUERY_KEY });
    },
    onError: (error) => {
      logger.error('Error updating notification template:', error);
    },
  });
};
